import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"
import SuperChart, { type DateChartSeries } from "./SuperChart"
import { useMemo } from "react"
import LoadingOverlay from "../LoadingOverlay"
import { addYears } from "date-fns"
import { startOfTodayUTC } from "~/utils/datetime"
import HeaderTooltip from "../table/tooltips/HeaderTooltip"
import { type CustomTooltipProps } from "ag-grid-react"
import { Card, CardContent, CardHeader, CardTitle } from "../Card"
import { ChartType } from "~/constants/graphs"
import { useChartSeriesConfig } from "~/constants/colors"
import { dateSeries } from "~/utils/graphUtils"
import { formatName } from "~/utils/namingUtils"

type Props = {
  customerIds: string[]
  variantIds: string[]
}

export const PreorderGraphTooltip = (props: CustomTooltipProps) => {
  if (props.location === "header") {
    return <HeaderTooltip {...props} />
  }
  const variantIds = props.node?.group ? props.value.variantIds : [props.data.variantId]
  const customerIds = props.node?.group ? props.value.customerIds : [props.data.customerId]
  return (
    <div className="w-[600px] mr-6 z-10">
      <PreorderGraph customerIds={customerIds} variantIds={variantIds} />
    </div>
  )
}

export default function PreorderGraph({ customerIds, variantIds }: Props) {
  const chartConfig = useChartSeriesConfig()
  const warehouseId = useSkuStore((state) => state.warehouseId)
  const { data: preorders, isLoading } = api.preOrder.preOrdersByCustomerIds.useQuery(
    { customerIds, variantIds, warehouseId },
    { enabled: !!customerIds.length && !!variantIds.length },
  )

  const preorderDates = preorders?.map((x) => x.deliveryDate)
  // get min date from preorders
  const minDate = !!preorderDates?.length
    ? preorderDates.reduce((acc, curr) => {
        return acc < curr ? acc : curr
      })
    : startOfTodayUTC()

  const maxDate = !!preorderDates?.length
    ? preorderDates.reduce((acc, curr) => {
        return acc > curr ? acc : curr
      })
    : addYears(startOfTodayUTC(), 2)

  const series: DateChartSeries = useMemo(
    () =>
      dateSeries(chartConfig.preOrder, {
        name: formatName("preorder", true, true),
        type: "column",
        data:
          preorders?.map((entry) => ({
            milliseconds: new Date(entry.deliveryDate).getTime(),
            value: entry.value,
          })) ?? [],
      }),
    [preorders, chartConfig],
  )

  if (isLoading)
    return (
      <Card>
        <CardHeader>
          <CardTitle>{formatName("preorder", true, true)}</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingOverlay />
        </CardContent>
      </Card>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle>{formatName("preorder", true, true)}</CardTitle>
      </CardHeader>
      <CardContent>
        <SuperChart
          type={ChartType.TimeSeries}
          data={[series]}
          dateRange={{ start: minDate, end: maxDate }}
        />
      </CardContent>
    </Card>
  )
}
